import { React } from 'react'
import { motion } from 'framer-motion'
import { useDocumentTitle, useDocumentConfig } from 'hooks/useDocument'
import Hero from 'modules/Hero'
import Button from 'components/Button'
import ServicesList from 'components/ServicesList'
import './ServicesPages.scss'

const WordPressEcommerce = () => {
	useDocumentTitle(`WordPress & E-commerce - Sites et Boutiques en Ligne Sur-Mesure`)
	useDocumentConfig(`blue`, false)

	return (
		<motion.main
			className='service-page'
			initial={{ opacity: 0, transition: { duration: 0.3 } }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0, transition: { duration: 0.3 } }}
		>
			<Hero
				scene={2}
				scrollTo='Découvrir nos solutions'
			>
				WordPress &<br />
				E-commerce
			</Hero>

			<section className='service-intro'>
				<div className='container'>
					<h1 className='align-center'>WordPress & E-commerce</h1>
					<p className='lead align-center'>
						Créez votre présence en ligne avec des sites WordPress performants et des boutiques e-commerce qui convertissent
					</p>
				</div>
			</section>

			<section className='service-content'>
				<div className='container'>
					<div className='content-grid'>
						<div className='content-main'>
							<h2>Solutions WordPress et e-commerce professionnelles</h2>
							<p>
								Spécialistes WordPress et e-commerce, nous créons des sites web et boutiques en ligne 
								sur-mesure qui allient performance, design et facilité de gestion. Que vous souhaitiez 
								un site vitrine élégant ou une plateforme e-commerce complète, nous adaptons la solution 
								à vos besoins spécifiques et votre budget.
							</p>

							<h3>Services WordPress</h3>
							<ul className='no-prefix'>
								<li><strong>Sites vitrine WordPress</strong> : Design unique, responsive et optimisé SEO</li>
								<li><strong>Thèmes sur-mesure</strong> : Développement from scratch ou personnalisation avancée</li>
								<li><strong>Plugins personnalisés</strong> : Fonctionnalités spécifiques à votre activité</li>
								<li><strong>Migration WordPress</strong> : Transfert sécurisé depuis d'autres plateformes</li>
								<li><strong>Optimisation performance</strong> : Temps de chargement &lt; 3 secondes garantis</li>
							</ul>

							<h3>Solutions E-commerce</h3>
							<div className='tech-grid'>
								<div className='tech-category'>
									<h4>WooCommerce</h4>
									<p>La solution e-commerce WordPress : flexible, extensible, idéale pour PME</p>
								</div>
								<div className='tech-category'>
									<h4>Shopify</h4>
									<p>Plateforme tout-en-un : rapide à déployer, gestion simplifiée, multi-canal</p>
								</div>
								<div className='tech-category'>
									<h4>PrestaShop</h4>
									<p>Open source puissant : catalogue illimité, multi-boutiques, B2B/B2C</p>
								</div>
								<div className='tech-category'>
									<h4>Solutions custom</h4>
									<p>Développement sur-mesure pour besoins spécifiques et grands volumes</p>
								</div>
							</div>

							<h3>Fonctionnalités e-commerce avancées</h3>
							<ul className='no-prefix'>
								<li>Gestion multi-devises et multi-langues</li>
								<li>Systèmes de paiement sécurisés (Stripe, PayPal, CB)</li>
								<li>Gestion des stocks en temps réel</li>
								<li>Programmes de fidélité et codes promo</li>
								<li>Marketplace et vendeurs multiples</li>
								<li>Intégration ERP et CRM</li>
								<li>Tableaux de bord analytics avancés</li>
								<li>Automatisation marketing (emails, abandons panier)</li>
							</ul>

							<h3>Optimisations incluses</h3>
							<ul className='no-prefix'>
								<li><strong>SEO e-commerce</strong> : Structure optimisée, rich snippets, sitemap produits</li>
								<li><strong>Performance mobile</strong> : Design mobile-first, AMP si pertinent</li>
								<li><strong>Sécurité renforcée</strong> : SSL, PCI DSS, protection anti-fraude</li>
								<li><strong>UX conversion</strong> : Parcours d'achat optimisé, checkout simplifié</li>
								<li><strong>Scalabilité</strong> : Architecture évolutive pour accompagner votre croissance</li>
							</ul>
						</div>

						<aside className='content-sidebar'>
							<div className='sidebar-box'>
								<h3>Avantages clés</h3>
								<ul className='benefits-list no-prefix'>
									<li>Administration intuitive</li>
									<li>100% personnalisable</li>
									<li>SEO-friendly natif</li>
									<li>Large communauté de support</li>
									<li>Coûts maîtrisés</li>
									<li>Formation incluse</li>
								</ul>
							</div>

							<div className='sidebar-box'>
								<h3>Tarifs WordPress</h3>
								<p className='price-info'>
									<strong>2500-5000€ HT</strong><br />
									Site WordPress complet
								</p>
								<p className='small'>
									Inclus : design, 5-10 pages, SEO de base, formation
								</p>
								<br />
								<p className='price-info'>
									<strong>6000-18000€ HT</strong><br />
									Boutique WooCommerce
								</p>
								<p className='small'>
									Selon nombre de produits et fonctionnalités
								</p>
							</div>

							<div className='sidebar-box'>
								<h3>Tarifs Shopify</h3>
								<p className='price-info'>
									<strong>3000-8000€ HT</strong><br />
									Boutique Shopify
								</p>
								<p className='small'>
									Setup, design, formation + abonnement Shopify
								</p>
							</div>

							<div className='sidebar-box'>
								<h3>Délais moyens</h3>
								<ul className='benefits-list no-prefix'>
									<li>Site vitrine : 3-4 semaines</li>
									<li>E-commerce simple : 4-6 semaines</li>
									<li>Marketplace : 8-12 semaines</li>
									<li>Migration : 2-3 semaines</li>
								</ul>
							</div>

							<div className='sidebar-box cta-box'>
								<h3>Projet WordPress ou Shopify ?</h3>
								<p>Discutons de votre site ou boutique en ligne</p>
								<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1' size='small'>
									Obtenir un devis
								</Button>
							</div>
						</aside>
					</div>
				</div>
			</section>

			<section className='service-process'>
				<div className='container'>
					<h2 className='align-center'>Notre process création WordPress/E-commerce</h2>
					<div className='process-steps'>
						<div className='process-step'>
							<span className='step-number'>01</span>
							<h3>Stratégie</h3>
							<p>Analyse besoins, choix plateforme, architecture</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>02</span>
							<h3>Design UX/UI</h3>
							<p>Maquettes, parcours utilisateur, identité visuelle</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>03</span>
							<h3>Développement</h3>
							<p>Intégration, fonctionnalités, tests qualité</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>04</span>
							<h3>Lancement</h3>
							<p>Migration, formation, support post-lancement</p>
						</div>
					</div>
				</div>
			</section>

			<ServicesList currentService='wordpress-ecommerce' />

			<div className='column-full align-center'>
				<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1'>
					Lancer votre projet
				</Button>
			</div>
		</motion.main>
	)
}

export default WordPressEcommerce